import React, {useCallback, useEffect, useRef, useState} from "react";
import {Checkbox, CircularProgress, IconButton, List, Stack} from '@mui/material';
import NotificationItem from "./NotificationItem";
import api from "../../../services/api";
import {createNotification} from "../../../reducer/actions/createNotification";
import toast from "react-hot-toast";
import Alert from "@mui/material/Alert/Alert";
import Tooltip from "@mui/material/Tooltip";
import {MarkAsUnreadRounded, MarkEmailReadRounded} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {SPSelectUserField} from "../../Table/fields/SPSelectUserField";
import {userByRole} from "../../../schema/userByRole";

interface NotificationListProps {
  openReservations: (row:any) => void
  openSidePane: (row:any) => void
  refresh: () => void
}

const NotificationList: React.FC<NotificationListProps> = ({ openReservations,refresh,openSidePane }) => {

  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState<any>(true);
  const [checked, setChecked] = useState<{ [key: string]: boolean }>({});
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [selectedBookingAgent, setSelectedBookingAgent] = useState<any>(null);

  const observer = useRef<IntersectionObserver | null>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(() => {
    setLoading(true);

    const searchParams = new URLSearchParams();
    searchParams.set('page', (page).toString());
    if (selectedBookingAgent){
      searchParams.set('booking_agent_id', selectedBookingAgent?.id);
    }

    api.get('/notifications/all',searchParams).then((result: any) => {

      if (result.data.length < 50) {
        setHasMore(false);
      }

      const unread = result.data.filter((n: any) => !n.seen);
      const read = result.data.filter((n: any) => n.seen);

      setNotifications((prev:any) => [...prev, ...unread, ...read]); // Egymás után fűzi az új adatokat
      setLoading(false);

      if (result.show) {
        createNotification(`You have ${result.new} unread notification`, ``);
        toast(`You have ${result.new} unread notification`, {
          icon: '🔔',
        });
      }


      const newChecked: { [key: string]: boolean } = {};

      result.data.forEach((notification: any) => {
        if (notification.group_id) {
          newChecked[notification.group_id] = false;
        }
      });

      setChecked(newChecked);
      setCheckedAll(false);

    });
  },[page, selectedBookingAgent]);

  const lastNotificationRef = useCallback(
    (node: any) => {

      if (loading || !hasMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

          debounceTimeout.current = setTimeout(() => {
            setPage((prev) => prev + 1);
          }, 300);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchNotifications();
    }, 500);

    return () => clearTimeout(handler);
  }, [page, selectedBookingAgent]);

  const handleClickNotification = (notification:any) => {

    if (notification?.type === 'service-reservation'){
      openReservations({
        name: notification?.group?.name,
        group: notification?.group,
        ids: notification?.service_reservation_ids,
      });

      api.post(`/notifications/group`,{
        ids: [notification?.group_id],
        value: true
      }).then((result: any) => {
        fetchNotifications();
        refresh();
      });
    }

    if (
      notification?.type === 'service-reservation-message' ||
      notification?.type === 'service-reservation-provider-modification'
    ){

      api.post(`/notifications/${notification?.id}`,{

      }).then((result: any) => {
        fetchNotifications();
        refresh();

        navigate(`/service-reservations/${notification?.service_reservation_id}`);

      });
    }

  }
  const handleClickAllCheckedNotification = (value:any) => {

    const checkedGroups:any = [];
    Object.keys(checked).map((item:any)=>{
      if (checked[item]){
        checkedGroups.push(item);
      }
    })

    api.post(`/notifications/group`,{
      ids: checkedGroups,
      value: value,
    }).then((result: any) => {
      fetchNotifications();
      refresh();
    });

  }
  const handleSelectBookingAgent = (value:any) => {

    setNotifications([]);
    setPage(1);
    setSelectedBookingAgent(value);

  }

  useEffect(() => {

    if (notifications){
      const newChecked: { [key: string]: boolean } = {};
      notifications.forEach((notification: any) => {
        if (notification.group_id) {
          newChecked[notification.group_id] = checkedAll;
        }
      });
      setChecked(newChecked);
    }

  }, [checkedAll]);

  return (
   <>
     <Stack>

       <Stack
         sx={{
           paddingX: '15px',
           paddingTop: '10px',
           backgroundColor: 'white',
           position: 'sticky',
           top: 0,
           zIndex: 10,
           borderBottom: '1px solid #ccc'
         }}
         justifyContent={'space-between'}
         direction={'row'}
       >
         <Stack direction={'row'} gap={0}>
           <Tooltip title={'Check all'}>
             <Checkbox
               edge="start"
               checked={checkedAll}
               tabIndex={-1}
               disableRipple
               onChange={(event, checked) => {
                 setCheckedAll(checked);
               }}
             />
           </Tooltip>
           <Tooltip title={'Mark all as read'}>
             <IconButton onClick={() => { handleClickAllCheckedNotification(true); }}>
               <MarkEmailReadRounded color={'primary'} />
             </IconButton>
           </Tooltip>
           <Tooltip title={'Mark all as unread'}>
             <IconButton onClick={() => { handleClickAllCheckedNotification(false); }}>
               <MarkAsUnreadRounded color={'primary'} />
             </IconButton>
           </Tooltip>
         </Stack>

         <Stack minWidth={'250px'}>
           <SPSelectUserField
             title={'Booking Agent'}
             onChange={(value) => {
               handleSelectBookingAgent(value);
             }}
             schema={userByRole('100')}
             defaultValue={undefined}
             forcedValue={undefined}
             disabled={false}
             isMissing={false}
             accordingToValue={undefined}
           />
         </Stack>
       </Stack>

       <List sx={{ width: "500px" , maxHeight: "350px" }}>

         {loading && (
          <Stack alignItems={'center'} width={'100%'}>
            <CircularProgress />
          </Stack>
         )}

         {(notifications.length === 0 && !loading) && (
           <Alert severity="info">No notifications</Alert>
         )}

         {(notifications.length && !loading) ?
           notifications.map((notification: any, index: number) => (
             <div ref={index === notifications.length - 1 ? lastNotificationRef : null} key={index}>
               <NotificationItem
                 checked={checked[notification?.group_id]}
                 onCheck={(value: any) => setChecked({ ...checked, [notification?.group_id]: value })}
                 notification={notification}
                 handleClickNotification={handleClickNotification}
               />
             </div>
           )) : <></>
         }
       </List>

       {loading && (
         <Stack direction="row" justifyContent="center">
           <CircularProgress size="30px" />
         </Stack>
       )}

     </Stack>

   </>
  );
};

export default NotificationList;